import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { MapPin, Phone, Mail, Clock, Facebook, Instagram, Youtube, Twitter } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_health-portal-129/artifacts/t64300ho_SH%20Logo.png";

const Footer = () => {
  const { t } = useLanguage();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const quickLinks = [
    { id: 'home', label: t('nav.home') },
    { id: 'about', label: t('nav.about') },
    { id: 'specialities', label: t('nav.specialities') },
    { id: 'facilities', label: t('nav.facilities') },
    { id: 'appointment', label: t('nav.bookAppointment') },
  ];

  const departments = [
    t('specialities.departments.obgyn.name'),
    t('specialities.departments.cardio.name'),
    t('specialities.departments.urology.name'),
    t('specialities.departments.ortho.name'),
    t('specialities.departments.gastro.name'),
    t('specialities.departments.medicine.name'),
  ];

  return (
    <footer className="footer py-16" data-testid="footer">
      <div className="section-container">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl p-3 inline-block mb-6">
              <img 
                src={LOGO_URL} 
                alt="Sakthi Hospital Logo" 
                className="h-10 w-auto object-contain"
              />
            </div>
            <p className="text-sm text-white/70 mb-4">{t('footer.tagline')}</p>
            <p className="text-sm text-white/60 leading-relaxed mb-6">
              {t('footer.description')}
            </p>
            
            {/* Social Links */}
            <div className="flex items-center gap-3">
              <a 
                href="https://www.facebook.com/sakthihospitals" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-[#00a0e3] transition-colors"
                data-testid="social-facebook"
              >
                <Facebook size={18} />
              </a>
              <a 
                href="https://www.instagram.com/sakthi_hospital/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-[#00a0e3] transition-colors"
                data-testid="social-instagram"
              >
                <Instagram size={18} />
              </a>
              <a 
                href="https://www.youtube.com/channel/UCLHTOwTj_J3MiSaSNXTDRWw" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-[#00a0e3] transition-colors"
                data-testid="social-youtube"
              >
                <Youtube size={18} />
              </a>
              <a 
                href="https://in.linkedin.com/company/sakthihospitals" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-[#00a0e3] transition-colors"
                data-testid="social-linkedin"
              >
                <Twitter size={18} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-['Poppins'] font-semibold text-white mb-6">{t('footer.quickLinks')}</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => scrollToSection(link.id)}
                    className="text-sm text-white/70 hover:text-[#00a0e3] transition-colors"
                    data-testid={`footer-link-${link.id}`}
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Departments */}
          <div>
            <h4 className="font-['Poppins'] font-semibold text-white mb-6">{t('footer.departments')}</h4>
            <ul className="space-y-3">
              {departments.map((dept) => (
                <li key={dept}>
                  <button
                    onClick={() => scrollToSection('specialities')}
                    className="text-sm text-white/70 hover:text-[#00a0e3] transition-colors"
                  >
                    {dept}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-['Poppins'] font-semibold text-white mb-6">{t('footer.contact')}</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-[#00a0e3] flex-shrink-0 mt-0.5" />
                <div className="text-sm text-white/70">
                  <p>{t('contact.address.line1')}</p>
                  <p>{t('contact.address.line2')}</p>
                  <p>{t('contact.address.line3')}</p>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={18} className="text-[#00a0e3] flex-shrink-0" />
                <div className="text-sm text-white/70">
                  <p>{t('contact.phone.main')}</p>
                  <p>{t('contact.phone.emergency')}</p>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={18} className="text-[#00a0e3] flex-shrink-0" />
                <span className="text-sm text-white/70">{t('contact.email.address')}</span>
              </li>
              <li className="flex items-start gap-3">
                <Clock size={18} className="text-[#00a0e3] flex-shrink-0 mt-0.5" />
                <div className="text-sm text-white/70">
                  <p>{t('contact.hours.opd')}</p>
                  <p>{t('contact.hours.emergency')}</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-12 pt-8 text-center">
          <p className="text-sm text-white/60">{t('footer.copyright')}</p>
        </div>

        {/* SEO Content - Location keywords for search engines */}
        <div className="mt-8 pt-6 border-t border-white/5">
          <p className="text-[10px] text-white/20 leading-relaxed max-w-4xl mx-auto text-center">
            Sakthi Hospital is the best hospital in Chennai located in Triplicane, near Royapettah, Mylapore, Chepauk, Mandaveli, Alwarpet, T. Nagar, Egmore, Nungambakkam, Thousand Lights and Anna Salai. 
            We are the top-rated multi-specialty hospital in Central Chennai offering the best gynaecology hospital, best cardiology hospital, best urology hospital, best orthopaedic hospital, 
            best gastroenterology hospital, best ENT hospital, and best paediatrics hospital in Chennai. 
            Our services include 24/7 emergency care, ICU, NICU, dialysis, laparoscopic surgery, joint replacement, maternity and normal delivery, IVF, 
            kidney stone treatment, heart treatment, and comprehensive health check-ups. 
            NABH accredited hospital with 75+ beds, ambulance service, pharmacy, physiotherapy, and cashless insurance facility. 
            Best doctors in Chennai. Affordable healthcare. Hospital near me in Triplicane.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
